import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  AfterViewInit,
} from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, map, startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import { DateTime } from 'luxon';
import { StationPickerComponent } from '@components/station-picker/station-picker.component';
import { TravelerPickerComponent } from '@components/traveler-picker/traveler-picker.component';
import { TripTypePickerComponent } from '@components/trip-type-picker/trip-type-picker.component';
import { BottomImageModalComponent } from '@components/modals/bottom-image-modal/bottom-image-modal.component';
import { DatesPickerComponent } from '@components/dates-picker/dates-picker.component';
import { LetDirective } from '@directives/app-let.directive';
import {
  DATE_FORMAT_DEFAULT,
  DATE_FORMAT_YEAR_MONTH_DATE,
} from '@constants/date-format.constants';
import {
  DEFAULT_MAX_DATE,
  DEFAULT_MIN_DATE,
  MULTI_CITY_MAX_TRIP_COUNT,
  TripType,
} from '@constants/home.constants';
import {
  COUNTRY_CODE_US,
  CultureKeyType,
  FALLBACK_CULTURE,
  MAX_INFANT_AGE_YEARS,
  FALLBACK_CURRENCY,
  CULTURE_ROUTE_PARAM,
} from '@constants/app.constants';
import {
  PassengerCriteria,
  PassengerTypesCount,
  SearchRequest,
  StationCriteria,
} from '@models/requests/search-request.model';
import { Station } from '@models/responses/station-response.model';
import { PassengerTypeCode } from '@models/booking.models';
import { NullableString } from '@models/app.models';
import {
  DatesPicker,
  StationControl,
  StationPicker,
} from 'src/app/interfaces/search-widget-interface';
import { ModalService } from '@services/modal/modal.service';
import { SearchWidgetFormConfigService } from '@services/form-configs/search-widget-form-config/search-widget-form-config.service';
import { createDateFromDateLike, getCultureCodeParts } from '@utils/data-utils';
import {
  availabilityChangePassengers,
  availabilityGetRouteSchedule,
  availabilityInvalidSearchByQueryParams,
  availabilityRedirectToDohop,
  availabilitySearchAll,
  availabilitySearchAllHome,
  availabilitySearchAllModify,
  availabilitySearchByQueryParams,
  setIsSearchByQueryState,
  setSearchTripType,
} from '@actions/availability.action';
import {
  selectAppAutoSearch,
  selectPnrExists,
  selectShowModifySearchWidget,
} from '@selectors/app.selector';
import {
  selectDohopFeatureStations,
  selectN0StationsWithConnections,
  selectN0StationsWithConnectionsNoMacs,
  selectStationsState,
  selectStationsStateNoMacs,
  selectStationsWithConnectionsState,
  selectStationsWithConnectionsStateNoMacs,
} from '@selectors/station.selector';
import {
  selectSearchInputCriteriaState,
  selectSearchInputPassengerState,
  selectSearchInputState,
  selectAvailabilityTripTypeState,
  selectHasInvalidPromoCode,
  selectRouteSchedule,
  selectRecentScheduleNotFound
} from '@selectors/availability.selector';
import { SearchWidgetCopy } from '@models/cms/search-widget.model';
import { FeatureFlagService } from '@services/feature-flag/feature-flag.service';
import {
  selectGeolocatedCountryCode,
  selectSelectedCurrencyCode,
} from '@selectors/culture.selector';
import {
  selectIsFlightsPage,
  selectIsHomePage,
  selectIsModifyPath,
  selectRouteParam,
} from '@selectors/router.selector';
import { DEFAULT_SCHEDULE_LOADED_THRU_MESSAGE } from '@constants/search-widget.constants';
import { AutoSearch } from '@models/auto-search.model';
import {
  CalendarDateCssClass,
  RouteSchedule,
  RouteScheduleDate,
} from '@models/requests/route-schedule-request.model';
import { FeatureFlagsEnum } from '@models/environment.type';
import { DateFormats } from '@constants/date-format.constants';
import {
  SafeHtmlPipe,
  SanitizerValueType,
} from '@pipes/safe-html/safe-html.pipe';
import { concatLatestFrom } from '@ngrx/operators';
import { TravelerPickerModalComponent } from '@components/modals/traveler-picker-modal/traveler-picker-modal.component';
import { GrowthBookService } from '@services/growthbook/growthbook.service';
import { GrowthBookFeatureKeys } from '@models/growthbook.models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SvgIconComponent } from '@components/svg-icon/svg-icon.component';
import { WindowService } from '@services/window/window.service';
import { StandardConfirmationMessageModalComponent } from '@components/modals/standard-confirmation-message-modal/standard-confirmation-message-modal.component';
import { DohopService } from '@services/do-hop/do-hop.service';
import { InputComponent } from '@components/controls/input/input.component';
import { InputDirective } from '@directives/input/input.directive';
import { CapitalizeDirective } from '@directives/capitalize/capitalize.directive';
import { eachDayOfInterval } from '@utils/date-utils';
import { DateRangePickerService } from '@components/controls/date-range-picker/date-range-picker.service';
import { AppCopy } from '@models/cms/app.model';
import { StationNamePipe } from '@pipes/station-name/station-name.pipe';
import { DateRangePickerComponent } from '@components/controls/date-range-picker/date-range-picker.component';
import { SearchService } from 'src/app/features/search/services/search.service';
import { PreviousSearchComponent } from 'src/app/features/search/components/previous-search/previous-search.component';
import { TolstoyService } from "@services/tolstoy/tolstoy.service";
import { UtilitiesService } from '@services/utilities/utilities.service';
import { ConfirmationMessageModalComponent } from '@components/modals/confirmation-message-modal/confirmation-message-modal.component';
import { RouterLink } from "@angular/router";

@Component({
  selector: 'app-search-widget',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LetDirective,
    DatesPickerComponent,
    StationPickerComponent,
    TravelerPickerComponent,
    TripTypePickerComponent,
    SafeHtmlPipe,
    SvgIconComponent,
    InputComponent,
    InputDirective,
    CapitalizeDirective,
    StationNamePipe,
    DateRangePickerComponent,
    PreviousSearchComponent,
    RouterLink,
  ],
  templateUrl: './search-widget.component.html',
  styleUrls: ['./search-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchWidgetComponent implements OnInit, OnDestroy, AfterViewInit {
  private destroyRef = inject(DestroyRef);
  private dateRangePickerService = inject(DateRangePickerService);

  @ViewChild(TravelerPickerComponent)
  travelerPickerComponent: TravelerPickerComponent;
  /**
   * True on flights page, false elsewhere (10/20/2022 - Kyle H).
   */
  @Input() isFlightsPage = false;
  @Input() isHomePage = true;
  @Input() isMulticityPage = false;
  @Input() cmsCopy: SearchWidgetCopy;
  @Input() appCopy: AppCopy;
  @Input() background: boolean = false;

  searchWidgetForm: FormGroup;
  private dateRangePickerDepartDate$ = new BehaviorSubject<DateTime | null>(
    null,
  );
  private destinationStationChanges$ = new Subject<void>();

  stations$ = this.featureFlagService.isFeatureActive(
    FeatureFlagsEnum.ALLOW_MACS,
  )
    ? this.store.select(selectStationsState)
    : this.store.select(selectStationsStateNoMacs);
  stationsWithConnections$ = this.featureFlagService.isFeatureActive(
    FeatureFlagsEnum.ALLOW_MACS,
  )
    ? this.store.select(selectStationsWithConnectionsState)
    : this.store.select(selectStationsWithConnectionsStateNoMacs);
  norseStations$ = this.featureFlagService.isFeatureActive(
    FeatureFlagsEnum.ALLOW_MACS,
  )
    ? this.store.select(selectN0StationsWithConnections)
    : this.store.select(selectN0StationsWithConnectionsNoMacs);
  dohopStations$ = this.store.select(selectDohopFeatureStations);
  searchInput$ = this.store.select(selectSearchInputState);
  tripType$ = this.store.select(selectAvailabilityTripTypeState);
  isModifyPath$ = this.store.select(selectIsModifyPath);
  isFlightsPage$ = this.store.select(selectIsFlightsPage);
  isHomePage$ = this.store.select(selectIsHomePage);
  doesPnrExist$ = this.store.select(selectPnrExists);
  autoSearch$ = this.store.select(selectAppAutoSearch);
  culture$ = this.store.select(
    selectRouteParam(CULTURE_ROUTE_PARAM),
  ) as Observable<CultureKeyType | undefined>;
  lastSearchedCriteria$ = this.store.select(selectSearchInputCriteriaState);
  routeSchedule$ = this.store.select(selectRouteSchedule);
  recentScheduleNotFound$ = this.store.select(selectRecentScheduleNotFound);
  selectedCurrencyCode$ = this.store.select(selectSelectedCurrencyCode);
  geolocatedCountryCode$ = this.store.select(selectGeolocatedCountryCode);
  isModifyWidget$ = this.store.select(selectShowModifySearchWidget);
  passengers$ = this.store.select(selectSearchInputPassengerState);
  hasInvalidPromoCode$ = this.store.select(selectHasInvalidPromoCode);

  activeStations: Station[] | null = null;
  stations: Station[] | null = null;
  stationsWithConnections: Station[] | null = null;
  departDates: (Date[] | undefined)[] = [];
  returnDates: Date[] | undefined;
  departDateClasses: Array<CalendarDateCssClass[] | undefined> = [];
  returnDateClasses: CalendarDateCssClass[] | undefined;
  selectedRouteSchedule: Array<RouteScheduleDate[] | undefined> = [];
  onDestinationChangedSubmitTriggered = false;
  formInitialized = false;

  onDestroy$ = new Subject<void>();
  onScheduleExists$ = new BehaviorSubject<boolean>(false);
  onScheduleSet$ = new BehaviorSubject<boolean>(false);

  readonly FeatureFlagsEnum = FeatureFlagsEnum;
  readonly FALLBACK_CULTURE = FALLBACK_CULTURE;
  readonly DEFAULT_MIN_DATE = DEFAULT_MIN_DATE;
  readonly DEFAULT_MAX_DATE = DEFAULT_MAX_DATE;
  readonly DateTime = DateTime;
  readonly DateFormats = DateFormats;
  readonly SanitizerValueType = SanitizerValueType;
  readonly gbf = GrowthBookFeatureKeys;
  readonly dateFormats = DateFormats;
  readonly TripType = TripType;

  format = DATE_FORMAT_DEFAULT;
  minDates: Date[] = [];
  selectedOriginStation = [''];
  selectedOriginStationCode = [''];
  selectedDestinationStation = [''];
  selectedDestinationStationCode = [''];
  outSideClick = false;
  lastSearchClicked = false;
  childDobs: Date[] = [];
  infantDobs: Date[] = [];
  searchRequest: SearchRequest | null = null;
  tripType: TripType;
  private isStickyFlightEnabled: boolean =
    this.gbs.growthBook.isOn(
      this.gbf.FEAT_US561_STICKY_FLIGHT_SEARCH_ON_FRONT_PAGE,
    ) &&
    this.gbs.growthBook.getFeatureValue(
      this.gbf.FEAT_US561_STICKY_FLIGHT_SEARCH_ON_FRONT_PAGE,
      '',
    ) === 'b';

  readonly isTravelersStart =
    this.gbs.growthBook.isOn(this.gbf.FEAT_US458_TRAVELERS_FIRST_IN_FLOW) &&
    ['b', 'c'].includes(
      this.gbs.growthBook.getFeatureValue(
        this.gbf.FEAT_US458_TRAVELERS_FIRST_IN_FLOW,
        '',
      ),
    );

  readonly isSeatsBeforeBags = this.gbs.isVariantOfExperimentEnabled('feat-002379-seatmap-PremiumUpgrade', 'b');

  private isPromoOnRoundTripEnabled: boolean =
    this.gbs.growthBook.isOn(this.gbf.FEAT_US902_PROMO_CODE_ON_ROUND_TRIP) &&
    (
      this.gbs.growthBook.getFeatureValue(this.gbf.FEAT_US902_PROMO_CODE_ON_ROUND_TRIP, '') === 'b' ||
      this.gbs.growthBook.getFeatureValue(this.gbf.FEAT_US902_PROMO_CODE_ON_ROUND_TRIP, '') === 'c' ||
      this.gbs.growthBook.getFeatureValue(this.gbf.FEAT_US902_PROMO_CODE_ON_ROUND_TRIP, '') === 'd' ||
      this.gbs.growthBook.getFeatureValue(this.gbf.FEAT_US902_PROMO_CODE_ON_ROUND_TRIP, '') === 'e'
    )
  private isPromoOnRoundTripVariant: string = this.gbs.growthBook.getFeatureValue(this.gbf.FEAT_US902_PROMO_CODE_ON_ROUND_TRIP, '');

  readonly isDateRangePickerEnabled: boolean =
    this.gbs.growthBook.isOn(
      this.gbf.FEAT_US515_DATEPICKER_SEARCH_WIDGET_FRONTPAGE,
    ) &&
    this.gbs.growthBook.getFeatureValue(
      this.gbf.FEAT_US515_DATEPICKER_SEARCH_WIDGET_FRONTPAGE,
      '',
    ) === 'b';

  private featUS572IsOn = this.gbs.growthBook.isOn(this.gbf.FEAT_US572_PROMOCODE_FRONT_CAMPAIGNS);
  private featUS572Value = this.gbs.growthBook.getFeatureValue(this.gbf.FEAT_US572_PROMOCODE_FRONT_CAMPAIGNS, '');

  get tripTypePromoCntl(): FormGroup {
    return <FormGroup>this.searchWidgetForm.get('tripTypePromoCode');
  }

  get tripTypeCntl(): FormControl {
    return <FormControl>this.tripTypePromoCntl.get('tripType');
  }

  get promoCodeCntl(): FormControl {
    return <FormControl>this.tripTypePromoCntl.get('promoCode');
  }

  get criteriaCntl(): FormGroup {
    return <FormGroup>this.searchWidgetForm?.get('criteria');
  }

  get stationsCtrl(): FormArray {
    return <FormArray>this.criteriaCntl?.get('stations');
  }

  get datesCtrl(): FormArray {
    return <FormArray>this.criteriaCntl?.get('dates');
  }

  get travelersCtrl(): FormGroup {
    return <FormGroup>this.searchWidgetForm?.get('travelers');
  }

  get scheduleLoadedThru(): string {
    if (isNaN(+this.cmsCopy.scheduleLoadedThruDays)) {
      return this.cmsCopy.scheduleLoadedThruDate;
    }

    const thruDays = DateTime.now()
      .plus({ days: +this.cmsCopy.scheduleLoadedThruDays })
      .startOf('day');
    const thruDate = DateTime.fromFormat(
      this.cmsCopy.scheduleLoadedThruDate,
      DATE_FORMAT_YEAR_MONTH_DATE,
    ).startOf('day');

    return thruDays < thruDate
      ? thruDays.toFormat(DATE_FORMAT_YEAR_MONTH_DATE)
      : thruDate.toFormat(DATE_FORMAT_YEAR_MONTH_DATE);
  }

  get scheduleLoadedThruDateAsDate(): Date | undefined {
    return createDateFromDateLike(
      this.scheduleLoadedThru,
      DATE_FORMAT_YEAR_MONTH_DATE,
    );
  }

  get isDatePickerSingleDate(): Observable<boolean> {
    return this.tripTypeCntl.valueChanges.pipe(
      startWith(this.tripType),
      map((t) => t === TripType.oneWay)
    );
  }

  showPromoInput(cmsShowInput: boolean | null) {
    let showInput = cmsShowInput ?? false;

    if(this.featUS572IsOn) {
      switch (this.featUS572Value)
        {
          case 'a' :
            showInput = false;
            break;
          case 'b' :
          case 'c' :
            showInput = true;
            break;
          default:
            break;
        }
    }

    return showInput;
  }

  constructor(
    public searchService: SearchService,
    public modalService: ModalService,
    public store: Store,
    public featureFlagService: FeatureFlagService,
    private formConfigService: SearchWidgetFormConfigService,
    private cd: ChangeDetectorRef,
    public gbs: GrowthBookService,
    private doHopService: DohopService,
    private readonly tolstoyService: TolstoyService,
    private readonly utilitiesService: UtilitiesService
  ) {}

  ngOnInit(): void {
    this.gbs.onChange$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isStickyFlightEnabled =
          this.gbs.growthBook.isOn(this.gbf.FEAT_US561_STICKY_FLIGHT_SEARCH_ON_FRONT_PAGE)
          && this.gbs.growthBook.getFeatureValue(this.gbf.FEAT_US561_STICKY_FLIGHT_SEARCH_ON_FRONT_PAGE, '') === 'b';
        this.featUS572IsOn = this.gbs.growthBook.isOn(this.gbf.FEAT_US572_PROMOCODE_FRONT_CAMPAIGNS);
        this.featUS572Value = this.gbs.growthBook.getFeatureValue(this.gbf.FEAT_US572_PROMOCODE_FRONT_CAMPAIGNS, '');
        this.isPromoOnRoundTripEnabled =
          this.gbs.growthBook.isOn(this.gbf.FEAT_US902_PROMO_CODE_ON_ROUND_TRIP) &&
          (
            this.gbs.growthBook.getFeatureValue(this.gbf.FEAT_US902_PROMO_CODE_ON_ROUND_TRIP, '') === 'b' ||
            this.gbs.growthBook.getFeatureValue(this.gbf.FEAT_US902_PROMO_CODE_ON_ROUND_TRIP, '') === 'c' ||
            this.gbs.growthBook.getFeatureValue(this.gbf.FEAT_US902_PROMO_CODE_ON_ROUND_TRIP, '') === 'd' ||
            this.gbs.growthBook.getFeatureValue(this.gbf.FEAT_US902_PROMO_CODE_ON_ROUND_TRIP, '') === 'e'
          );
          this.isPromoOnRoundTripVariant = this.gbs.growthBook.getFeatureValue(this.gbf.FEAT_US902_PROMO_CODE_ON_ROUND_TRIP, '');
        if (this.searchWidgetForm) {
          this.setPromoCode();
        }
        this.cd.detectChanges();
      });

    combineLatest([
      this.stations$,
      this.stationsWithConnections$,
      this.culture$,
      this.autoSearch$,
      this.isHomePage$,
    ])
      .pipe(
        filter(
          ([stations, stationsWithConnections, culture]) =>
            !!stations &&
            (this.featureFlagService.isFeatureActive(
              FeatureFlagsEnum.CONNECTIONS,
            )
              ? !!stationsWithConnections
              : true) && !!culture,
        ),
        concatLatestFrom(() => [
          this.searchInput$,
          this.tripType$,
          this.lastSearchedCriteria$,
          this.selectedCurrencyCode$,
          this.geolocatedCountryCode$,
        ]),
        takeUntil(this.onDestroy$),
      )
      .subscribe(
        ([
          [
            stations,
            stationsWithConnections,
            culture,
            autoSearch,
            isHomePage
          ],
          searchReq,
          tripType,
          lastSearchedCriteria,
          selectedCurrencyCode,
          countryCode,
        ]) => {
          // Ensure that home page never shows multiple pickers.
          if (this.isMulticityPage) {
            this.tripType = TripType.multiCity;
          } else if (isHomePage) {
            if (tripType === TripType.oneWay) {
              this.tripType = TripType.oneWay;
            } else {
              this.tripType = TripType.roundTrip;
            }
          } else {
            this.tripType = tripType;
          }

          this.stations = stations ?? null;
          this.stationsWithConnections = stationsWithConnections ?? null;
          this.activeStations = this.featureFlagService.isFeatureActive(
            FeatureFlagsEnum.CONNECTIONS,
          )
            ? this.stationsWithConnections ?? this.stations
            : this.stations;
          if (searchReq && this.tripType) {
            this.searchRequest = searchReq;

            if (
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              !this.searchWidgetForm?.value.criteria.stations[0]
                .originStationCode ||
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              !this.searchWidgetForm?.value.criteria.stations[0]
                .destinationStationCode
            ) {
              this.searchWidgetForm =
                this.formConfigService.generateSearchWidgetForm(
                  this.activeStations ?? [],
                  searchReq,
                  this.tripType,
                );
            }

            if (
              this.tripType === TripType.multiCity &&
              searchReq?.criteria?.[0]?.beginDate
            ) {
              this.minDates = [...this.minDates];
              this.minDates[1] = DateTime.fromISO(
                searchReq.criteria[0].beginDate,
              ).toJSDate();
            }

            this.searchRequest.criteria?.forEach((c, i) => {
              const originStation = this.stations?.find(
                (s) => s.stationCode === c.origin,
              );

              const destStation = this.stations?.find(
                (s) => s.stationCode === c.destination,
              );

              originStation?.fullName &&
                originStation?.stationCode &&
                this.onOriginStationChange(
                  {
                    stationName: originStation.fullName,
                    stationCode: originStation.stationCode,
                  },
                  i,
                );
              destStation?.fullName &&
                destStation?.stationCode &&
                this.onDestinationStationChange(
                  {
                    stationName: destStation.fullName,
                    stationCode: destStation.stationCode,
                  },
                  i,
                );
            });
            this.infantDobs =
              searchReq.passengers
                ?.find((p) => p.type === PassengerTypeCode.INF)
                ?.dobs?.map((dob) => DateTime.fromISO(dob).toJSDate()) ?? [];

            this.childDobs =
              searchReq.passengers
                ?.find((p) => p.type === PassengerTypeCode.CHD)
                ?.dobs?.map((dob) => DateTime.fromISO(dob).toJSDate()) ?? [];
          } else if (!this.searchWidgetForm) {
            this.searchWidgetForm =
              this.formConfigService.generateSearchWidgetForm(
                this.activeStations ?? [],
                undefined,
                this.tripType
              );
          }
          if (autoSearch.search && culture && (!this.isHomePage || this.lastSearchClicked) && !this.isMulticityPage) {
            // search success will set this back to false in the reducer
            this.onSubmit(
              selectedCurrencyCode,
              culture,
              lastSearchedCriteria,
              countryCode ?? undefined,
              autoSearch,
            );
          }

          if (this.searchWidgetForm) {
            for (let i = 0; i < MULTI_CITY_MAX_TRIP_COUNT; i++) {
              this.minDates.push(
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                i > 0 && this.getDepartDateCntl(i - 1)?.value
                  ? this.getDepartDateCntl(i - 1).value
                  : DEFAULT_MIN_DATE,
              );
            }

            this.tripTypeCntl.valueChanges
              ?.pipe(takeUntil(this.onDestroy$))
              .subscribe((s: TripType) => this.tripTypeChange(s));

            if (this.tripType !== tripType) {
              this.tripTypeCntl.setValue(this.tripType);
            }
            this.setPromoCode();
            this.cd.detectChanges();
            this.formInitialized = true;
          }
        },
      );


    this.recentScheduleNotFound$
      .pipe(
        filter((recentScheduleNotFound) => recentScheduleNotFound),
        takeUntil(this.onDestroy$)
      )
      .subscribe((_) => {
        this.onScheduleSet$.next(true);
      });

    combineLatest([this.routeSchedule$, this.onScheduleExists$])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(([schedule]) => {
        const selectedStations =
          this.stationsCtrl?.getRawValue() as StationPicker[];
        selectedStations?.forEach((stations, i) => {
          this.selectedRouteSchedule[i] =
            schedule?.[
              `${stations.originStationCode}-${stations.destinationStationCode}`
            ];
          if (this.selectedRouteSchedule[i]) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            this.departDates[i] = this.selectedRouteSchedule[i]!.map(
              (d) => d.scheduleDay,
            );
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            this.departDateClasses[i] = this.selectedRouteSchedule[i]!.map(
              (d) => ({
                date: d.scheduleDay,
                classes: d.isDohop ? ['day-dohop'] : ['day-norse'],
              }),
            );

            const departCtrl = this.getDepartDateCntl(i);
            const returnCtrl = this.getReturnDateCntl(i);
            const prevDepartCtrl = i > 0  ? this.getDepartDateCntl(i -1) : null;
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const firstFlight = !prevDepartCtrl ? this.selectedRouteSchedule[i]![0] : this.selectedRouteSchedule[i]!.find(d => d.scheduleDay > prevDepartCtrl.value);
            const returnFlight = firstFlight && schedule?.[`${stations.destinationStationCode}-${stations.originStationCode}`]?.find(s => s.scheduleDay > firstFlight.scheduleDay);
            if (firstFlight) {
              departCtrl.setValue(firstFlight.scheduleDay);
              //Updates minDate validtion for return date
              this.cd.detectChanges();
              returnCtrl.setValue(returnFlight?.scheduleDay ?? firstFlight.scheduleDay);
              this.cd.detectChanges();

              if (this.tripTypeCntl?.value === TripType.oneWay || this.tripTypeCntl?.value === TripType.roundTrip && returnFlight) {
                this.onScheduleSet$.next(true);
              }

            }
          } else {
            this.departDates[i] = undefined;
            this.departDateClasses[i] = undefined;
          }
        });

        this.cd.detectChanges();
      });

      this.resetDateRangePickerOnChangesInputs();
  }

  private setPromoCode(): void {
    let autofillPromo: boolean = false;

    if(this.cmsCopy.promoInputPromoCode && this.cmsCopy.promoInputPromoStartDate && this.cmsCopy.promoInputPromoEndDate) {
      const cmsStartDateString: string = this.cmsCopy.promoInputPromoStartDate + 'Z';
      const cmsEndDateString: string = this.cmsCopy.promoInputPromoEndDate + 'Z';

      const promoStartDate: Date = new Date(cmsStartDateString);
      const promoEndDate: Date = new Date(cmsEndDateString);
      const current: Date = new Date(Date.now());

      if(promoStartDate < current && promoEndDate > current) {
        autofillPromo = true;
      }
    }

    if(this.featUS572IsOn) {
      switch (this.featUS572Value)
      {
        case 'a' :
          //add promo from cms has value
          if(autofillPromo) {
            this.promoCodeCntl.setValue(this.cmsCopy.promoInputPromoCode);
          }
          break;
        case 'b' :
          // promo code input is open, default functionality
          break;
        case 'c' :
          //add promo from cms if not already added to the criteria
          if(autofillPromo && !this.promoCodeCntl?.value) {
            this.promoCodeCntl.setValue(this.cmsCopy.promoInputPromoCode);
          }
          break;
        default:
          break;
      }
    }
    else {
      //if gb not running, code & dates are present, code isn't already in the search criteria, control is visible
      if(this.cmsCopy.promoInputShow && autofillPromo && !this.promoCodeCntl?.value) {
        this.promoCodeCntl.setValue(this.cmsCopy.promoInputPromoCode);
      }
    }
  }

  ngAfterViewInit(): void {
    this.hasInvalidPromoCode$
      .pipe(
        filter((x) => x),
        takeUntil(this.onDestroy$),
      )
      .subscribe((hasInvalidPromoCode) => {
        if (hasInvalidPromoCode) {
          this.promoCodeCntl.reset();
        }
      });

    this.isHomePage$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((isHomePage) => {
        if (isHomePage) {
          this.onDestinationChangedSubmitTriggered = false;
        }
      });
  }

  getStationsCtrl(index: number): FormGroup {
    return <FormGroup>this.stationsCtrl.get(`${index}`);
  }

  isValidStationCtrl(index: number): boolean {
    return this.getStationsCtrl(index).valid;
  }

  getStationOriginStationCode(index: number): string {
    return (
      (this.getStationsCtrl(index)?.get('originStationCode')
        ?.value as string) ?? ''
    );
  }

  getStationDestinationStationCode(index: number): string {
    return (
      (this.getStationsCtrl(index)?.get('destinationStationCode')
        ?.value as string) ?? ''
    );
  }

  getDatesCtrl(index: number): FormGroup {
    return <FormGroup>this.datesCtrl.get(`${index}`);
  }

  getTravelersCtrl() {
    return this.travelersCtrl?.controls;
  }

  getDepartDateCntl(index: number): FormControl {
    return <FormControl>this.getDatesCtrl(index)?.get('departDate');
  }

  getReturnDateCntl(index: number): FormControl {
    return <FormControl>this.getDatesCtrl(index)?.get('returnDate');
  }

  onOriginStationChange(event: StationControl, index: number): void {
    this.selectedOriginStation[index] = event?.stationName;
    this.selectedOriginStationCode[index] = event?.stationName
      ? event?.stationCode
      : '';
    this.fetchRouteSchedule(index);
  }

  onDestinationStationChange(event: StationControl, index: number): void {
    this.selectedDestinationStation[index] = event?.stationName;
    this.selectedDestinationStationCode[index] = event?.stationName
      ? event?.stationCode
      : '';

    this.onScheduleSet$.next(false);
    this.destinationStationChanges$.next();
    this.fetchRouteSchedule(index);

    this.triggerOnSubmit(index);
  }

  private triggerOnSubmit(index: number) {
    combineLatest([this.autoSearch$, this.searchInput$])
      .pipe((take(1)))
      .subscribe(([autoSearch, searchInput]) => {
        if (!this.formInitialized) {
          return;
        }

        if (!this.selectedOriginStationCode[index] || !this.selectedDestinationStationCode[index]) {
          return;
        }

        if (!this.checkIfRemoveCtaButtonFeatureIsOn(autoSearch.search, searchInput)) {
          return;
        }

        if (this.onDestinationChangedSubmitTriggered) {
          return;
        }

        this.onDestinationChangedSubmitTriggered = true;

        this.waitForRouteScheduleUpdate()
          .pipe(
            switchMap(() =>
              combineLatest([
                this.lastSearchedCriteria$,
                this.selectedCurrencyCode$,
                this.culture$,
                this.geolocatedCountryCode$,
                this.isModifyPath$
              ])
            ),
            take(1)
          ).subscribe((
          [
            lastSearchedCriteria,
            selectedCurrencyCode,
            culture,
            geolocatedCountryCode,
            isModifyPath
          ]
        ) => {
          this.onScheduleSet$.next(false);

          this.getStationsCtrl(index).updateValueAndValidity();
          this.getReturnDateCntl(index).updateValueAndValidity();

          this.onSubmit(
            selectedCurrencyCode,
            culture ?? FALLBACK_CULTURE,
            lastSearchedCriteria ?? undefined,
            geolocatedCountryCode ?? undefined,
            undefined,
            isModifyPath ?? false
          );
        });
      });
  }

  public checkIfRemoveCtaButtonFeatureIsOn(isFromMetaSearch: boolean, searchInput: SearchRequest | null) {
    if (isFromMetaSearch || !this.isHomePage || searchInput?.criteria.some(c => c.destination)) {
      return false;
    }

    if (this.gbs.growthBook.isOn(this.gbf.FEAT_US515_DATEPICKER_SEARCH_WIDGET_FRONTPAGE)
        && this.gbs.growthBook.getFeatureValue(this.gbf.FEAT_US515_DATEPICKER_SEARCH_WIDGET_FRONTPAGE, '') === 'b') {
      return false;
    }

    return this.gbs.growthBook.isOn(this.gbf.FEAT_498_REMOVE_CTA_SEARCH_FRONTPAGE)
      && this.gbs.growthBook.getFeatureValue(this.gbf.FEAT_498_REMOVE_CTA_SEARCH_FRONTPAGE, '') === 'b';
  }

  private waitForRouteScheduleUpdate() {
    return this.onScheduleSet$.pipe(
      filter(value => value),
      take(1),
    );
  }



  onDepartDateChange(index: number): void {
    /* istanbul ignore else */
    if (this.tripTypeCntl?.value === TripType.multiCity) {
      this.minDates = [...this.minDates];
      for (let i = index; i < MULTI_CITY_MAX_TRIP_COUNT; i++) {
        if (i < this.datesCtrl.length) {
          if (i > 0) {
            const previousDateCtrl = this.getDepartDateCntl(i - 1);
            this.minDates[i] = <Date>previousDateCtrl.value;
            const dateCtrl = this.getDepartDateCntl(i);
            if (
              DateTime.fromJSDate(<Date>dateCtrl.value).startOf('day') <
              DateTime.fromJSDate(<Date>previousDateCtrl.value).startOf('day')
            ) {
              dateCtrl.reset();
            }
          } else {
            this.minDates[i] = DEFAULT_MIN_DATE;
          }
        }
      }
    } else if (this.tripTypeCntl?.value === TripType.roundTrip) {
      const departDateCtrl = this.getDepartDateCntl(index);
      const returnDateCtrl = this.getReturnDateCntl(index);
      if (
        DateTime.fromJSDate(<Date>returnDateCtrl.value).startOf('day') <
        DateTime.fromJSDate(<Date>departDateCtrl.value).startOf('day')
      ) {
        returnDateCtrl.reset();
      }
    }
  }

  fetchRouteSchedule(index: number) {
    const originStationCode = this.selectedOriginStationCode[index];
    const destinationStationCode = this.selectedDestinationStationCode[index];
    if (originStationCode && destinationStationCode) {
      this.routeSchedule$.pipe(take(1)).subscribe((s) => {
        if (!s?.[`${originStationCode}-${destinationStationCode}`]) {
          if (originStationCode !== destinationStationCode) {
            this.store.dispatch(
              availabilityGetRouteSchedule({
                request: {
                  origin: originStationCode,
                  destination: destinationStationCode,
                  beginDate: DateTime.now()
                    .startOf('day')
                    .toFormat(DATE_FORMAT_YEAR_MONTH_DATE),
                  endDate: this.scheduleLoadedThru,
                },
              }),
            );
          }
        } else {
          this.onScheduleExists$.next(true);
        }
        if (this.tripTypeCntl.value === TripType.roundTrip && index != 1) {
          if (!s?.[`${destinationStationCode}-${originStationCode}`]) {
            this.store.dispatch(
              availabilityGetRouteSchedule({
                request: {
                  origin: destinationStationCode,
                  destination: originStationCode,
                  beginDate: DateTime.now()
                    .startOf('day')
                    .toFormat(DATE_FORMAT_YEAR_MONTH_DATE),
                  endDate: this.scheduleLoadedThru,
                },
              }),
            );
          }
        }
      });
    }
  }

  onDateRangePickerChanges(
    index: number,
    dates: { firstDate?: DateTime; secondDate?: DateTime; isSkipped: boolean },
  ) {
    this.getFirstDateForDateRangePicker(index)
      .pipe(take(1))
      .subscribe((d) => {
        this.getDepartDateCntl(index).setValue(
          dates.isSkipped || (!dates.firstDate && !dates.secondDate) ?
            d?.toJSDate() :
            dates.firstDate?.toJSDate(),
        );

        this.getReturnDateCntl(index).setValue(
          dates.isSkipped || (!dates.firstDate && !dates.secondDate) ?
            d?.toJSDate() :
            dates.secondDate?.toJSDate(),
        );

        this.dateRangePickerDepartDate$.next(
          dates.firstDate && dates.secondDate ? null : dates.firstDate ?? null,
        );

        this.getReturnDateCntl(index).updateValueAndValidity();
      });
  }

  onSubmit(
    currencyCode: NullableString,
    culture: CultureKeyType,
    previousCriteria: StationCriteria[] | undefined,
    countryCode: string | undefined,
    autoSearch?: AutoSearch,
    isModifyPath?: boolean,
  ): void {
    if (!autoSearch || !autoSearch.search) {
      this.store.dispatch(setIsSearchByQueryState({ state: false }));
    } else {
      const _dates: DatesPicker[] = [{
        departDate: new Date(),
        returnDate: new Date(),
      }];
      previousCriteria?.map((c, i) => {
        const jsBeginDate = DateTime.fromFormat(c.beginDate, DATE_FORMAT_YEAR_MONTH_DATE, { locale: culture}).toJSDate();
        this.setDatesPickerDates(_dates, jsBeginDate, i);
      });
      this.datesCtrl.setValue(_dates);
    }

    const selectedDates = this.datesCtrl.value as DatesPicker[];

    let earliestDepartureDate =
      createDateFromDateLike(selectedDates[0]?.departDate, this.format) ??
      new Date();

    let lastDepartureDate =
      (this.tripTypeCntl?.value === TripType.roundTrip
        ? createDateFromDateLike(selectedDates[0]?.returnDate, this.format)
        : createDateFromDateLike(selectedDates[0]?.departDate, this.format)) ??
      new Date();

    if (selectedDates?.length > 1) {
      // MultiCity
      selectedDates.forEach((d) => {
        const departDate =
          createDateFromDateLike(d?.departDate, this.format) ?? new Date();
        earliestDepartureDate =
          earliestDepartureDate < departDate
            ? earliestDepartureDate
            : departDate;
        lastDepartureDate =
          lastDepartureDate > departDate ? lastDepartureDate : departDate;
      });
    }

    const maxSearchDate = this.scheduleLoadedThru
      ? DateTime.fromFormat(
          this.scheduleLoadedThru,
          DATE_FORMAT_YEAR_MONTH_DATE,
        )
          .plus({ days: 1 })
          .toJSDate()
      : DEFAULT_MAX_DATE;

    if (selectedDates.some((d) => d.departDate >= maxSearchDate)) {
      this.onDestinationChangedSubmitTriggered = false;
      this.modalService.show(BottomImageModalComponent, {
        initialState: {
          btnText: this.cmsCopy.modify,
          text: (
            this.cmsCopy.scheduleLoadedThruMessage ??
            DEFAULT_SCHEDULE_LOADED_THRU_MESSAGE
          ).replace(
            '{scheduleLoadedThruDate}',
            DateTime.fromFormat(
              this.scheduleLoadedThru ?? DEFAULT_MAX_DATE.toDateString(),
              DATE_FORMAT_YEAR_MONTH_DATE,
            ).toFormat(DATE_FORMAT_DEFAULT),
          ),
          img: this.cmsCopy.modalBottomImage.url,
          altText: this.cmsCopy.modalBottomImage.altText,
        },
      });
      return;
    }

    /* istanbul ignore else */
    if (!this.searchWidgetForm.valid) {
      /* istanbul ignore else */
      if (this.travelersCtrl.invalid) {
        this.onDestinationChangedSubmitTriggered = false;
        this.modalService.show(BottomImageModalComponent, {
          initialState: {
            btnText: this.cmsCopy.select,
            text: this.cmsCopy.paxValidationError,
            img: this.cmsCopy.modalBottomImage.url,
            altText: this.cmsCopy.modalBottomImage.altText,
          },
        });
        return;
      }

      /* istanbul ignore else */
      if (this.stationsCtrl.invalid) {
        this.onDestinationChangedSubmitTriggered = false;
        const modalRef = this.modalService.show(BottomImageModalComponent, {
          initialState: {
            btnText: this.cmsCopy.okayCopy,
            text: this.cmsCopy.invalidStations,
            img: this.cmsCopy.modalBottomImage.url,
            altText: this.cmsCopy.modalBottomImage.altText,
          },
        });

        if(autoSearch?.search) {
          modalRef.onHide?.pipe(take(1)).subscribe(_ => {
            this.store.dispatch(availabilityInvalidSearchByQueryParams());
          })
        }
        return;
      }

      /* istanbul ignore else */
      if (this.datesCtrl.invalid) {
        this.onDestinationChangedSubmitTriggered = false;
        this.modalService.show(BottomImageModalComponent, {
          initialState: {
            btnText: this.cmsCopy.return,
            text: this.cmsCopy.invalidDates,
            img: this.cmsCopy.modalBottomImage.url,
            altText: this.cmsCopy.modalBottomImage.altText,
          },
        });
        return;
      }

      return;
    }

    if (this.isFlightsPage) {
      const adultsCount = this.travelersCtrl.controls.adultCount?.value ?? 0 as unknown as number;
      const youthCount = this.travelersCtrl.controls.youthCount?.value ?? 0 as unknown as number;
      if (adultsCount + youthCount < 1) {
        return;
      }
      if (adultsCount === 0 && youthCount > 0) {
        const confirmationRef = this.modalService.show(
          ConfirmationMessageModalComponent,
          {
            class: 'modal-dialog-centered md-confirmation',
            initialState: {
              confirmBtnText: this.cmsCopy.agedEligibleConfirmation,
              declineBtnText: this.cmsCopy.goBack,
              messageText: this.cmsCopy.agedEligibleMessage
            },
          },
        );
        confirmationRef.content?.response.pipe(take(1)).subscribe((response) => {
          confirmationRef.hide();
          if (!response) {
            return;
          }
          this.prepareSearchCriteria(currencyCode, culture, previousCriteria, countryCode, selectedDates, autoSearch, isModifyPath);
        });

        return;
      }
    }

    this.prepareSearchCriteria(currencyCode, culture, previousCriteria, countryCode, selectedDates, autoSearch, isModifyPath);

  }

  prepareSearchCriteria(currencyCode: NullableString, culture: CultureKeyType,  previousCriteria: StationCriteria[] | undefined,  countryCode: string | undefined, selectedDates: DatesPicker[], autoSearch?: AutoSearch,  isModifyPath?: boolean ) {
    const selectedStations = this.stationsCtrl.value as StationPicker[];
    const criteria: StationCriteria[] = selectedStations.map((s, i) => ({
      origin: s.originStationCode,
      destination: s.destinationStationCode,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      beginDate: DateTime.fromJSDate(
        createDateFromDateLike(selectedDates[i]?.departDate, this.format) ??
          new Date(),
      ).toISODate()!,
    }));

    let routeDatesValid = true;
    criteria.forEach((c) => {
      let message = this.getRouteMessage(c, culture);
      if (!message) {
        message = this.getRestrictionMessage(c, countryCode);
      }
      if (message) {
        routeDatesValid = false;
        this.onDestinationChangedSubmitTriggered = false;
        this.modalService.show(BottomImageModalComponent, {
          initialState: {
            btnText: this.cmsCopy.modify,
            text: message,
            img: this.cmsCopy.modalBottomImage.url,
            altText: this.cmsCopy.modalBottomImage.altText,
          },
        });
        return;
      }
    });

    if (!routeDatesValid) return;

    /* istanbul ignore else */
    if (this.tripTypeCntl?.value === TripType.roundTrip) {
      criteria.push(
        ...selectedStations.map((s, i) => ({
          origin: s.destinationStationCode,
          destination: s.originStationCode,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          beginDate: DateTime.fromJSDate(
            createDateFromDateLike(selectedDates[i]?.returnDate, this.format) ??
              new Date(),
          ).toISODate()!,
        })),
      );
    }

    const isAnyPairDohop =
      this.featureFlagService.isFeatureActive(FeatureFlagsEnum.CONNECTIONS) &&
      criteria.some((ond) => {
        const isMarketDohop = !!this.activeStations
          ?.find((s) => s.stationCode === ond.origin)
          ?.validMarkets?.find((m) => m.stationCode === ond.destination)
          ?.isDohop;

        return isMarketDohop;
      });

    const isAnyRouteScheduleDateNorse =
      this.featureFlagService.isFeatureActive(FeatureFlagsEnum.CONNECTIONS) &&
      criteria.some((ond, i) => {
        const routeScheduleDepartDate = this.selectedRouteSchedule[i]?.find(
          (s) => {
            const scheduleDayString = DateTime.fromJSDate(s.scheduleDay)
              .startOf('day')
              .toFormat(DATE_FORMAT_YEAR_MONTH_DATE);
            return scheduleDayString === ond.beginDate;
          },
        );
        return !routeScheduleDepartDate?.isDohop;
      });

    this.handleSearch(
      currencyCode,
      culture,
      criteria,
      previousCriteria,
      autoSearch,
      isAnyPairDohop,
      isAnyRouteScheduleDateNorse,
      isModifyPath,
    );
  }

  getFirstDateForDateRangePicker(i: number) {
    const cityPair = `${this.selectedOriginStationCode[i]}-${this.selectedDestinationStationCode[i]}`;
    return this.getAvailableDatesForDateRangePicker(cityPair).pipe(
      map((d) => d[0]),
    );
  }

  getLastDateForDateRangePicker(i: number) {
    const cityPair = `${this.selectedDestinationStationCode[i]}-${this.selectedOriginStationCode[i]}`;
    return this.getAvailableDatesForDateRangePicker(cityPair).pipe(
      map((d) => d[d.length - 1]),
    );
  }

  getDisabledDatesForDepartDate(i: number) {
    return this.routeSchedule$.pipe(
      map((routeSchedule) => {
        const cityPair = `${this.selectedOriginStationCode[i]}-${this.selectedDestinationStationCode[i]}`;
        return this.getDisabledDatesForDateRangePickerByCityPair(
          routeSchedule,
          cityPair,
        );
      }),
    );
  }

  getDisabledDatesForReturnDate(i: number) {
    return this.routeSchedule$.pipe(
      map((routeSchedule) => {
        const cityPair = `${this.selectedDestinationStationCode[i]}-${this.selectedOriginStationCode[i]}`;
        return this.getDisabledDatesForDateRangePickerByCityPair(
          routeSchedule,
          cityPair,
        );
      }),
    );
  }

  private setDatesPickerDates(dates: DatesPicker[], date: Date, index: number) {
    if (index === 0) {
      dates[0]!.departDate = date;
      dates[0]!.returnDate = date;
    } else if (index === 1 && this.tripTypeCntl.value === TripType.roundTrip) {
      dates[0]!.returnDate = date;
    } else {
      dates.push({
        departDate: date,
        returnDate: date,
      });
    }
  }

  private handleSearch(
    currencyCode: NullableString,
    culture: CultureKeyType,
    criteria: StationCriteria[],
    previousCriteria: StationCriteria[] | undefined,
    autoSearch: AutoSearch | undefined,
    isAnyPairDohop: boolean,
    isAnyRouteScheduleDateNorse: boolean,
    isModifyPath: boolean | undefined,
  ) {
    if (
      isAnyPairDohop &&
      this.gbs.growthBook.isOn(this.gbf.FEAT_US366_DOHOP_V2_TOP_CONNECTIONS) &&
      ['b', 'c'].includes(
        this.gbs.growthBook.getFeatureValue(
          this.gbf.FEAT_US366_DOHOP_V2_TOP_CONNECTIONS,
          '',
        ),
      )
    ) {
      this.showConnectionsModal(currencyCode, culture, criteria);
      return;
    }

    if (this.isHomePage || this.isMulticityPage) {
      this.handleHomePageSearch(
        currencyCode,
        culture,
        criteria,
        previousCriteria,
      );
      return;
    }

    this.handleFlightsPageSearch(
      criteria,
      currencyCode,
      autoSearch,
      isAnyPairDohop,
      isAnyRouteScheduleDateNorse,
      isModifyPath,
    );
  }

  private handleHomePageSearch(
    currencyCode: NullableString,
    culture: CultureKeyType,
    criteria: StationCriteria[],
    previousCriteria: StationCriteria[] | undefined,
  ) {
    this.lastSearchClicked
      ? this.dispatchHomeSearchAction(
          this.recalulateDobs(),
          criteria,
          currencyCode,
          previousCriteria,
        )
      : this.showTravelersPickerModal(
          culture,
          criteria,
          (passengers) => {
            this.dispatchHomeSearchAction(
              passengers,
              criteria,
              currencyCode,
              previousCriteria,
            );
            this.onDestinationChangedSubmitTriggered = true;
          },
          () => {
            this.onDestinationChangedSubmitTriggered = false;
          },
        );
    this.lastSearchClicked = false;
  }

  private dispatchHomeSearchAction(
    passengers: PassengerCriteria[],
    criteria: StationCriteria[],
    currencyCode: NullableString,
    previousCriteria: StationCriteria[] | undefined,
  ) {
    const actionPayload = {
      payload: {
        criteria,
        passengers: passengers,
        currencyCode: currencyCode ?? FALLBACK_CURRENCY,
        promotionCode: this.addPromoCodeForReturnTrip(),
        clearBooking: true,
        childDobs: this.childDobs,
        infantDobs: this.infantDobs,
      } as SearchRequest,
      tripType: this.tripTypeCntl?.value as TripType,
      isTravelersStart: this.isTravelersStart,
      isSeatsBeforeBags: this.isSeatsBeforeBags,
    };

    this.showOneWayFlightToUsaModalIfOneWayFlightToUsa$(
      criteria,
      previousCriteria,
    )
      .pipe(
        switchMap(() => this.dateRangePickerService.isSkipped$),
        take(1),
      )
      .subscribe((isSkipped) => {
        if (
          this.isDateRangePickerEnabled &&
          !isSkipped &&
          this.dateRangePickerService.firstDateFromRange$.getValue()
        ) {
          this.dispatchAvailabilitySearchByQueryParams(criteria, passengers);
          this.dateRangePickerService.onResetState({});
        }
        this.store.dispatch(availabilitySearchAllHome(actionPayload));
        this.utilitiesService.updateFlightSearchActionStarted();
      });
  }

  private dispatchAvailabilitySearchByQueryParams = (
    criteria: StationCriteria[],
    passengers: PassengerCriteria[],
  ) => {
    const actionPayload = {
      payload: {
        criteria,
        passengers,
        currencyCode: FALLBACK_CURRENCY,
        clearBooking: true,
      } as SearchRequest,
      isTravelersStart: this.isTravelersStart,
      isSeatsBeforeBags: this.isSeatsBeforeBags,
      tripType: this.tripTypeCntl?.value as TripType,
    };
    this.store.dispatch(availabilitySearchByQueryParams(actionPayload));
  };

  private addPromoCodeForReturnTrip(): string {
    if (this.promoCodeCntl.value) {
      const promoValue: string = this.promoCodeCntl.value as string;
      this.promoCodeCntl.setValue(promoValue.toUpperCase(), { emitEvent: false })
    }
    if (
      this.isPromoOnRoundTripEnabled &&
      !this.promoCodeCntl.value &&
      this.tripTypeCntl?.value === TripType.roundTrip
    ) {
      switch (this.isPromoOnRoundTripVariant) {
        case 'b':
          return 'RTD';
        case 'c':
          return 'RTD5';
        case 'd':
          return 'RTD10';
        case 'e':
          return 'RTD20';
        default:
          return ''
      }
    } else {
      return this.promoCodeCntl.value as string
    }
  }

  private handleFlightsPageSearch(
    criteria: StationCriteria[],
    currencyCode: NullableString,
    autoSearch: AutoSearch | undefined,
    isAnyPairDohop: boolean,
    isAnyRouteScheduleDateNorse: boolean,
    isModifyPath: boolean | undefined,
  ) {
    const passengers = this.recalulateDobs();

    const isValid = this.validate();

    if (!isValid) {
      this.store.dispatch(availabilityInvalidSearchByQueryParams());
      return;
    }

    const audultPaxCountInStore =
      this.searchRequest?.passengers?.find(
        (p) => p.type === PassengerTypeCode.ADT,
      )?.count ?? 0;
    const childPaxCountInStore =
      this.searchRequest?.passengers?.find(
        (p) => p.type === PassengerTypeCode.CHD,
      )?.count ?? 0;
    const infantPaxCountInStore =
      this.searchRequest?.passengers?.find(
        (p) => p.type === PassengerTypeCode.INF,
      )?.count ?? 0;

    const adultPaxCountCurrent =
      passengers?.find((p) => p.type === PassengerTypeCode.ADT)?.count ?? 0;
    const childPaxCountCurrent =
      passengers?.find((p) => p.type === PassengerTypeCode.CHD)?.count ?? 0;
    const infantPaxCountCurrent =
      passengers?.find((p) => p.type === PassengerTypeCode.INF)?.count ?? 0;

    const isPaxTypeCountMatching =
      audultPaxCountInStore === adultPaxCountCurrent &&
      childPaxCountInStore === childPaxCountCurrent &&
      infantPaxCountInStore === infantPaxCountCurrent;
    const isTripTypeMatching = this.tripType === this.tripTypeCntl?.value;

    // TODO: Important - Cover these cases with specific tests so it will withstand with future code changes.
    // If in Home page then always clear the booking.
    // If in Flights page Modify flow and pax type count mismatch between store and local changes then clear the booking.
    const clearBooking =
      !this.isFlightsPage ||
      (this.isFlightsPage && !isPaxTypeCountMatching) ||
      (this.isFlightsPage && !isTripTypeMatching) ||
      autoSearch?.clearBooking;

    const actionPayload = {
      payload: {
        childDobs: this.childDobs,
        infantDobs: this.infantDobs,
        criteria: criteria.map((c, i) => ({
          ...c,
          productClass:
            this.featureFlagService.isFeatureActive(
              FeatureFlagsEnum.BUNDLE_RESTRICTION,
            ) && !clearBooking
              ? this.searchRequest?.criteria[i]?.productClass
              : undefined,
        })),
        passengers: passengers,
        currencyCode: currencyCode ?? FALLBACK_CURRENCY,
        promotionCode: this.addPromoCodeForReturnTrip(),
        clearBooking,
        isTravelersStart: this.isTravelersStart,
        isSeatsBeforeBags: this.isSeatsBeforeBags,
      } as SearchRequest,
      tripType: this.tripTypeCntl?.value as TripType,
    };

    if (
      !this.featureFlagService.isFeatureActive(FeatureFlagsEnum.CONNECTIONS) ||
      (this.featureFlagService.isFeatureActive(FeatureFlagsEnum.CONNECTIONS) &&
        !isAnyPairDohop &&
        isAnyRouteScheduleDateNorse)
    ) {
      if (this.isHomePage || this.isMulticityPage) {
        this.store.dispatch(availabilitySearchAllHome(actionPayload));
        this.utilitiesService.updateFlightSearchActionStarted();
      } else if (this.isFlightsPage) {
        this.store.dispatch(
          isModifyPath
          ? availabilitySearchAllModify(actionPayload)
          : availabilitySearchAll(actionPayload),
        );
        this.utilitiesService.updateFlightSearchActionStarted();
      }
    } else {
      this.activeStations &&
        this.store.dispatch(
          availabilityRedirectToDohop({
            ...actionPayload,
            stations: this.activeStations,
          }),
        );
    }
  }

  private getRestrictionMessage(
    c: StationCriteria,
    countryCode: string | undefined,
  ): NullableString {
    if (!this.cmsCopy || !countryCode) {
      return null;
    }

    const countryRestriction = this.cmsCopy.countryStationsRestriction.find(
      (r) => r.countryCode === countryCode,
    );
    if (!countryRestriction) {
      return null;
    }

    const originRestriction = countryRestriction.restrictedStations.find(
      (s) => s.stationCode === c.origin,
    );
    const destinationRestriction = countryRestriction.restrictedStations.find(
      (s) => s.stationCode === c.destination,
    );
    let message: NullableString;
    if (!originRestriction && !destinationRestriction) {
      return null;
    } else {
      if (originRestriction?.restrictOrigin) {
        message = countryRestriction.message;
      }
      if (destinationRestriction?.restrictDestination) {
        message = countryRestriction.message;
      }
    }

    return message;
  }

  private getRouteMessage(
    c: StationCriteria,
    culture?: NullableString,
  ): NullableString {
    if (!this.cmsCopy) {
      return null;
    }

    const routeMessage = this.cmsCopy.routeMessages.find(
      (r) => r.origin === c.origin && r.destination === c.destination,
    );

    if (!routeMessage || (!routeMessage.startDate && !routeMessage.endDate)) {
      return null;
    }

    let message: NullableString = null;
    if (
      (!routeMessage.startDate ||
        new Date(routeMessage.startDate) <= new Date(c.beginDate)) &&
      routeMessage.endDate &&
      (routeMessage.startDate
        ? new Date(routeMessage.startDate) <= new Date(routeMessage.endDate)
        : true) &&
      new Date(routeMessage.endDate) < new Date(c.beginDate)
    ) {
      message = routeMessage.endMessage;
    } else if (
      (!routeMessage.endDate ||
        new Date(routeMessage.endDate) < new Date(c.beginDate) ||
        (routeMessage.startDate
          ? new Date(routeMessage.startDate) <= new Date(routeMessage.endDate)
          : true)) &&
      routeMessage.startDate &&
      new Date(routeMessage.startDate) > new Date(c.beginDate)
    ) {
      message = routeMessage.startMessage;
    } else {
      return null;
    }

    const routeOrgin =
      this.activeStations?.find(
        (station: Station) => station.stationCode == routeMessage.origin,
      ) ?? null;

    const routeDestination =
      this.activeStations?.find(
        (station: Station) => station.stationCode == routeMessage.destination,
      ) ?? null;

    return (
      message &&
      message
        .replace(
          '{origin}',
          `${routeOrgin?.fullName ?? ''} (${
            routeOrgin?.stationCode ?? routeMessage.origin
          })`,
        )
        .replace(
          '{destination}',
          `${routeDestination?.fullName ?? ''} (${
            routeDestination?.stationCode ?? routeMessage.destination
          })`,
        )
        .replace(
          '{startDate}',
          routeMessage.startDate
            ? DateTime.fromISO(routeMessage.startDate).toFormat(
                DATE_FORMAT_DEFAULT,
                {
                  locale: culture ?? undefined,
                },
              )
            : '',
        )
        .replace(
          '{endDate}',
          routeMessage.endDate
            ? DateTime.fromISO(routeMessage.endDate).toFormat(
                DATE_FORMAT_DEFAULT,
                {
                  locale: culture ?? undefined,
                },
              )
            : '',
        )
    );
  }

  private tripTypeChange(tripType: TripType): void {
    if (tripType === TripType.multiCity) {
      if (this.stationsCtrl.length === 1) {
        this.stationsCtrl.push(
          this.formConfigService.generateStationsForm(
            this.activeStations ?? [],
          ),
        );
      }

      /* istanbul ignore else */
      if (tripType === TripType.multiCity) {
        if (
          this.featureFlagService.isFeatureActive(FeatureFlagsEnum.CONNECTIONS)
        ) {
          this.activeStations = this.stations;
        }
        this.minDates = [...this.minDates];
        for (let i = 0; i < MULTI_CITY_MAX_TRIP_COUNT; i++) {
          this.minDates[i] =
            i > 0 && this.getDepartDateCntl(i - 1)?.value
              ? <Date>this.getDepartDateCntl(i - 1).value
              : DEFAULT_MIN_DATE;
        }
      } else {
        if (
          this.featureFlagService.isFeatureActive(FeatureFlagsEnum.CONNECTIONS)
        ) {
          this.activeStations = this.stationsWithConnections;
        }
      }

      if (this.datesCtrl.length === 1 && this.minDates[1]) {
        this.datesCtrl.push(this.formConfigService.generateDatesForm(tripType));
      }
    }

    if (tripType !== TripType.multiCity) {
      if (this.stationsCtrl.length > 1) {
        this.stationsCtrl.removeAt(1);
      }
      if (this.datesCtrl.length > 1) {
        this.datesCtrl.removeAt(1);
      }
    }

    if (tripType === TripType.roundTrip) {
      this.datesCtrl.controls.forEach((group) => {
        group.get('returnDate')?.setValidators([Validators.required]);
      });
    } else {
      this.datesCtrl.controls.forEach((group) => {
        group.get('returnDate')?.clearValidators();
        group.get('returnDate')?.reset();
      });
    }

    this.stationsCtrl.controls.forEach((_, i) => {
      this.fetchRouteSchedule(i);
    });
    if ((this.isHomePage || this.isMulticityPage) && this.isStickyFlightEnabled) {
      this.store.dispatch(setSearchTripType({ tripType }));
    }
  }

  showOneWayFlightToUsaModalIfOneWayFlightToUsa$(
    criteria: StationCriteria[],
    previousCriteria: StationCriteria[] | undefined,
  ): Observable<unknown> {
    const isSameCriteria =
      previousCriteria?.every((previousCriterion, criterionIndex) => {
        const currentCriterion = criteria[criterionIndex];
        return (
          currentCriterion?.origin === previousCriterion.origin &&
          currentCriterion?.destination === previousCriterion.destination
        );
      }) ?? false;

    // Don't re-show modal for the same criteria they just searched,
    // when not on home page.
    // I.e. when on home page, show modal even if search criteria is the
    // same as the previous search.
    if (this.isFlightsPage && isSameCriteria) {
      return of(true);
    }

    const isOneWayFlightToTheUsa =
      criteria.length === 1 &&
      criteria.some((criterion) => {
        const destinationStation = this.activeStations?.find(
          (station) => station.stationCode === criterion.destination,
        );
        return (
          destinationStation?.locationDetails.countryCode === COUNTRY_CODE_US
        );
      });

    if (!isOneWayFlightToTheUsa) {
      return of(true);
    }

    const usFlightModalRef = this.modalService.show(BottomImageModalComponent, {
      initialState: {
        btnText: this.cmsCopy.okayCopy,
        text: this.cmsCopy.oneWayFlightToUsaCopy,
        img: this.cmsCopy.modalBottomImage.url,
        altText: this.cmsCopy.modalBottomImage.altText,
      },
    });

    return usFlightModalRef?.onHidden?.pipe(take(1)) ?? of(true);
  }

  onTravelersChipClick() {
    this.culture$.pipe(take(1)).subscribe((culture) => {
      const selectedStations = this.stationsCtrl.value as StationPicker[];
      const selectedDates = this.datesCtrl.value as DatesPicker[];
      const criteria: StationCriteria[] = selectedStations.map((s, i) => ({
        origin: s.originStationCode,
        destination: s.destinationStationCode,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        beginDate: DateTime.fromJSDate(
          createDateFromDateLike(selectedDates[i]?.departDate, this.format) ??
            new Date(),
        ).toISODate()!,
      }));
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const initialState = this.travelersCtrl.value;
      this.showTravelersPickerModal(
        culture ?? FALLBACK_CULTURE,
        criteria,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        (passengers) =>
          this.store.dispatch(availabilityChangePassengers({ passengers })),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        () => this.travelersCtrl.setValue(initialState),
      );
    });
  }

  showConnectionsModal(
    currency: NullableString,
    culture: CultureKeyType,
    criteria: StationCriteria[],
  ): void {
    const connectionsModalRef = this.modalService.show(
      StandardConfirmationMessageModalComponent,
      {
        class: 'modal-desktop-600',
        initialState: {
          primaryBtnText: this.cmsCopy.connectionsModalAccept,
          secondaryBtnText: this.cmsCopy.connectionsModalDecline,
          messageText: this.cmsCopy.connectionsModalText,
          titleText: this.cmsCopy.connectionsModalTitle,
        },
      },
    );

    this.onDestinationChangedSubmitTriggered = false;

    connectionsModalRef.content?.response.pipe(take(1)).subscribe((value) => {
      if (
        value === 'primaryClick' &&
        this.stationsWithConnections &&
        currency
      ) {
        const request = {
          criteria,
          passengers: [],
          currencyCode: currency ?? FALLBACK_CURRENCY,
          promotionCode: this.promoCodeCntl?.value as string,
          clearBooking: true,
          childDobs: this.childDobs,
          infantDobs: this.infantDobs,
        } as SearchRequest;
        WindowService.open(
          this.doHopService.getDohopUrl(
            request,
            this.tripType,
            currency,
            getCultureCodeParts(culture).language,
            this.stationsWithConnections,
          ),
        );

        this.onDestinationChangedSubmitTriggered = false;
      }
    });
  }

  private showTravelersPickerModal(
    culture: CultureKeyType,
    criteria: StationCriteria[],
    onSuccess: (passengers: PassengerCriteria[]) => void,
    onFailureOrCancel: () => void,
  ) {
    this.tolstoyService.hideTolstoyVideo();
    const travelerPickerModalRef = this.modalService.show(
      TravelerPickerModalComponent,
      {
        class: 'modal-desktop-600',
        initialState: {
          culture,
          cmsSearchWidgetCopy: this.cmsCopy,
          travelersFormGroup: this.travelersCtrl,
        },
      },
    );

    travelerPickerModalRef.onHidden
      ?.pipe(take(1))
      .subscribe((reason: string) => {
        if (reason === 'backdrop-click' || reason === 'esc') {
          onFailureOrCancel();
          return;
        }
      });

    travelerPickerModalRef.content?.response
      ?.pipe(take(1))
      ?.subscribe((response) => {
        if (!response) {
          onFailureOrCancel();
          return;
        }
        const passengers = this.recalulateDobs();

        const isValid = this.validate();
        if (!isValid) {
          onFailureOrCancel();
          return;
        }

        const hasInfant = this.infantDobs?.length > 0;
        if (
          hasInfant ||
          this.childDobs.some(
            (dob) =>
              criteria[0]?.beginDate &&
              DateTime.fromFormat(
                criteria[0].beginDate,
                DATE_FORMAT_YEAR_MONTH_DATE,
              ).diff(DateTime.fromJSDate(dob), 'years').years < 16,
          )
        ) {
          if (
            !passengers.some(
              (passenger) => passenger.type === PassengerTypeCode.ADT,
            )
          ) {
            this.modalService.show(BottomImageModalComponent, {
              initialState: {
                btnText: this.cmsCopy.modify,
                text: this.cmsCopy.unaccompaniedMinorError,
                img: this.cmsCopy.modalBottomImage.url,
                altText: this.cmsCopy.modalBottomImage.altText,
              },
            });
            onFailureOrCancel();
            return;
          }
        }

        onSuccess(passengers);
      });
  }

  private recalulateDobs(): PassengerCriteria[] {
    const travelers = this.travelersCtrl?.value as PassengerTypesCount;
    const passengers: PassengerCriteria[] = [];

    this.infantDobs = [];
    this.childDobs = [];

    const infantBirthYear = DateTime.utc().year - (MAX_INFANT_AGE_YEARS - 1);
    for (let i = 0; i < travelers.infantCount; i++) {
      this.infantDobs.push(DateTime.utc(infantBirthYear, 1, 1).toJSDate());
    }

    travelers.adultCount &&
      passengers.push({
        type: PassengerTypeCode.ADT,
        count: travelers.adultCount,
      });

    travelers.childCount &&
      passengers.push({
        type: PassengerTypeCode.CHD,
        count: travelers.childCount,
      });

    travelers.infantInOwnSeatCount &&
      passengers.push({
        type: PassengerTypeCode.INS,
        count: travelers.infantInOwnSeatCount,
      });

    travelers.youthCount &&
      passengers.push({
        type: PassengerTypeCode.OTN,
        count: travelers.youthCount,
      });

    this.infantDobs.length &&
      passengers.push({
        type: PassengerTypeCode.INF,
        count: this.infantDobs.length,
        dobs: this.infantDobs.map((dob) =>
          DateTime.fromJSDate(dob).toFormat(DATE_FORMAT_YEAR_MONTH_DATE),
        ),
      });

    return passengers;
  }

  validate(): boolean {
    const travelers = this.travelersCtrl?.value as PassengerTypesCount;

    const isAdtInfRatio =
      travelers.adultCount >=
      travelers.infantCount + travelers.infantInOwnSeatCount;
    if (!isAdtInfRatio) {
      this.modalService.show(BottomImageModalComponent, {
        initialState: {
          btnText: this.cmsCopy.okayCopy,
          text: 'We only allow one infant per adult.',
          img: this.cmsCopy.modalBottomImage.url,
          altText: this.cmsCopy.modalBottomImage.altText,
        },
      });
      return false;
    }

    const hasAdtWithChd = travelers.adultCount > 0 || travelers.childCount <= 0;
    if (!hasAdtWithChd) {
      this.modalService.show(BottomImageModalComponent, {
        initialState: {
          btnText: this.cmsCopy.okayCopy,
          text: this.cmsCopy.unaccompaniedMinorError,
          img: this.cmsCopy.modalBottomImage.url,
          altText: this.cmsCopy.modalBottomImage.altText,
        },
      });
      return false;
    }

    return true;
  }

  private getAvailableDatesForDateRangePicker(cityPair: string) {
    return this.routeSchedule$.pipe(
      map((routeSchedule) => {
        return (
          routeSchedule?.[cityPair]
            ?.filter((routeScheduleDate) => !routeScheduleDate.isDohop)
            .map((routeScheduleDate) =>
              DateTime.fromJSDate(routeScheduleDate.scheduleDay),
            ) ?? []
        );
      }),
    );
  }

  private getDisabledDatesForDateRangePickerByCityPair(
    routeSchedule: RouteSchedule | null,
    cityPair: string,
  ) {
    if (!routeSchedule) {
      return [];
    }

    const routeScheduleDates = routeSchedule[cityPair];

    if (!routeScheduleDates || routeScheduleDates.length === 0) {
      return [];
    }

    const firstScheduleDay = routeScheduleDates[0]?.scheduleDay;
    const lastScheduleDay =
      routeScheduleDates[routeScheduleDates.length - 1]?.scheduleDay;

    if (!firstScheduleDay || !lastScheduleDay) {
      return [];
    }

    const firstDate = DateTime.fromJSDate(firstScheduleDay);
    const lastDate = DateTime.fromJSDate(lastScheduleDay);
    const allDates = eachDayOfInterval(firstDate, lastDate);

    const availableDates = routeScheduleDates
      .filter((routeScheduleDate) => !routeScheduleDate.isDohop)
      .map((routeScheduleDate) =>
        DateTime.fromJSDate(routeScheduleDate.scheduleDay),
      );

    return allDates.filter(
      (date) =>
        !availableDates.map((d) => d.toMillis()).includes(date.toMillis()),
    );
  }

  private resetDateRangePickerOnChangesInputs() {
    combineLatest([
      this.destinationStationChanges$,
      this.tripType$,
    ])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.dateRangePickerService.onResetState({
          all: false,
          resetFirstDateFromRange: true,
          resetLastDateFromRange: true,
        });
      });
  }

  onCancel(): void {
    this.searchService.onCancelModifySearch();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
  }
}
